"use client"

import { useEffect, useId, useMemo, useState, type ReactNode } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CalculatorBookingCta } from "@/components/tools/calculator-booking-cta"
import { buttonVariants } from "@/components/ui/button"
import { useCountry } from "@/components/layout/country-provider"
import {
  defaultLipidUnit,
  fromMmolPerL,
  looksLikeMmolPerLEntry,
  offersLipidUnitSwitch,
  toMmolPerL,
  type LipidUnit,
} from "@/lib/lipid-units"
import { score2CalculatorKeys as k } from "@/i18n/score2-calculator-keys"
import { formatDecimal } from "@/lib/locale-number-format"
import { track } from "@/lib/analytics"
import { cn } from "@/lib/utils"
import {
  CHOLESTEROL_MG_DL_PER_MMOL_L,
  MAX_HDL,
  MAX_TOTAL_CHOLESTEROL,
  MIN_HDL,
  MIN_TOTAL_CHOLESTEROL,
  SCORE2_BANDS,
  SCORE2_MAX_AGE,
  SCORE2_MIN_AGE,
  score2AgeGroupFor,
  score2BandFor,
  score2Problem,
  score2RegionForCountry,
  score2Risk,
  roundRisk,
  type Score2AgeGroupDef,
  type Score2Band,
  type Score2Problem,
  type Score2Sex,
} from "@/lib/score2"

/**
 * The interactive part of /tools/score2-calculator.
 *
 * One centred column: the page intro, then the form, then the result panel,
 * which does not exist until Calculate has run. One component owns all three
 * because the form and the result share state; the `intro` slot is what keeps
 * the h1 in the client file, where the page's structure is meant to stay
 * readable.
 *
 * Deliberately framer-motion-free: the widget is always mounted, so any
 * animation here would have to clear the `<AnimatePresence>` + cacheComponents
 * trap (see `/animation-cache-trap`). CSS transitions cover what it needs.
 *
 * Nothing is submitted, stored or tracked. That matters more here than on the
 * BMI page: the six inputs are a blood pressure reading and two lipid values,
 * i.e. the contents of a lab report, and the arithmetic happens in the browser
 * and never leaves it.
 */

/**
 * The risk axis drawn under the result: 0 to 30%, which covers both age
 * groups' very-high thresholds (7.5% and 10%) with room to see how far past
 * one a result sits. Above 30 the marker clamps to the right edge.
 */
const SCALE_MAX = 30

/** Exported for the reference table's band chips in the client file. */
export const BAND_STYLES: Record<
  Score2Band,
  { bar: string; chip: string; accent: string }
> = {
  lowToModerate: {
    bar: "bg-deep-green-400",
    chip: "bg-deep-green-100 text-deep-green-800",
    accent: "text-deep-green-600",
  },
  high: {
    bar: "bg-terracotta-300",
    chip: "bg-terracotta-100 text-terracotta-900",
    accent: "text-terracotta-600",
  },
  veryHigh: {
    bar: "bg-terracotta-600",
    chip: "bg-terracotta-200 text-terracotta-950",
    accent: "text-terracotta-800",
  },
}

const inputClasses =
  "type-body-lg w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-neutral-900 tabular-nums focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-aubergine-400 focus-visible:outline-none"

const unitSuffixClasses =
  "type-body-md pointer-events-none absolute inset-y-0 right-4 flex items-center text-neutral-500"

const labelClasses = "type-label-md block text-neutral-700 normal-case"

const hintClasses = "type-body-sm mt-2 text-neutral-500"

/**
 * The two units the cholesterol pair can be entered in, in the order they are
 * offered: the market's own convention first, since that is what the fields
 * start on where this control renders at all.
 *
 * `as const` at module scope for the same reason `SEX_OPTIONS` below is:
 * annotating `labelKey` as `string` widens it out of the catalog's key union
 * and next-intl's typed `t()` rejects it.
 */
const CHOL_UNIT_OPTIONS = [
  { value: "mgPerDl", labelKey: k.calcUnitMgDl },
  { value: "mmolPerL", labelKey: k.calcUnitMmol },
] as const satisfies readonly {
  value: LipidUnit
  labelKey: MessageKeys<"score2Calculator">
}[]

/**
 * `as const`, and at module scope. Annotating `labelKey` as `string` widens it
 * out of the catalog's key union and next-intl's typed `t()` rejects it, the
 * same mistake that reddened the BMI page's first CI run.
 */
const SEX_OPTIONS = [
  { value: "female", labelKey: k.calcSexFemale },
  { value: "male", labelKey: k.calcSexMale },
] as const

const SMOKER_OPTIONS = [
  { value: "no", labelKey: k.calcSmokerNo },
  { value: "yes", labelKey: k.calcSmokerYes },
] as const

/**
 * NaN for a blank or unparseable field. `Number("")` is 0, which would read a
 * still-empty box as a real zero: a blank blood-pressure field would submit as
 * 0 mmHg and an empty HDL box as 0 mmol/L, both of which the model scores.
 */
function parseField(value: string): number {
  const trimmed = value.trim().replace(",", ".")
  if (trimmed === "") return NaN
  return Number(trimmed)
}

interface Score2Result {
  /** Rounded to the one decimal the reader sees, see `roundRisk`. */
  risk: number
  band: Score2Band
  ageGroup: Score2AgeGroupDef
}

export function Score2CalculatorWidget({ intro }: { intro?: ReactNode }) {
  const t = useTranslations("score2Calculator")
  // The risk percentage is rendered here rather than coming from the catalog,
  // so the decimal separator has to be chosen explicitly: a French page
  // printing "4.7%" beside prose that says "4,7 %" contradicts itself.
  const locale = useLocale()
  const { code: country } = useCountry()
  const fieldId = useId()

  /**
   * SCORE2 is calibrated per CVD risk region, and the region is a property of
   * the market, not of the reader. Resolved from the country rather than
   * hardcoded to Switzerland's so that opening the gate to another market
   * without wiring its region into `SCORE2_REGION_BY_COUNTRY` shows nothing
   * instead of quietly scoring a German reader on Swiss calibration, which
   * would understate their risk by roughly a quarter with no visible symptom.
   */
  const region = score2RegionForCountry(country)

  /**
   * Flipped in an effect, and stamped onto the root as `data-hydrated`.
   *
   * Not decoration: the inputs are controlled, so anything typed into them
   * before React owns them is silently discarded, and an e2e test has no other
   * way to know when that moment has passed. On the BMI page a blind retry
   * loop kept flaking on a loaded CI runner until this signal existed. One
   * boolean, renders nothing.
   */
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const [sex, setSex] = useState<Score2Sex>("female")
  const [smoker, setSmoker] = useState(false)
  const [age, setAge] = useState("")
  const [systolicBp, setSystolicBp] = useState("")
  const [totalCholesterol, setTotalCholesterol] = useState("")
  const [hdlCholesterol, setHdlCholesterol] = useState("")

  /**
   * The unit both cholesterol fields ask for, and whether the reader can
   * change it. One switch for the pair: they are the same molecule, so a
   * reader whose report gives one in mg/dL has the other in mg/dL too, and
   * two switches would only create a way to mix them.
   *
   * Both answers come from the market's laboratory convention
   * (lib/lipid-units.ts): German labs report cholesterol in mg/dL, Swiss ones
   * in mmol/L, and SCORE2's terms take mmol/L whatever the field says.
   * `useState`'s initialiser rather than an effect, so the static HTML
   * renders the right unit instead of flipping after hydration.
   */
  const [cholUnit, setCholUnit] = useState<LipidUnit>(() =>
    defaultLipidUnit(country)
  )
  const canSwitchCholUnit = offersLipidUnitSwitch(country)

  /**
   * The result only exists after Calculate, and any change to any field drops
   * it. A stale figure sitting beside edited inputs is the one thing a
   * side-by-side layout must not show, and it matters more here than on a BMI
   * page: this number is the one a reader might take to an appointment.
   */
  const [result, setResult] = useState<Score2Result | null>(null)
  /**
   * `Score2Problem` plus one case the model cannot see: a mmol/L figure typed
   * while mg/dL is the selected unit. Read as mg/dL it converts to about 0.13
   * mmol/L, which is merely low rather than impossible, and `score2Problem`
   * would answer it with "check your entries" over two values the reader
   * copied correctly.
   */
  const [problem, setProblem] = useState<
    Score2Problem | "cholMmolValue" | null
  >(null)

  /** Every field setter goes through this. */
  function invalidate() {
    setResult(null)
    setProblem(null)
  }

  /**
   * Switching the unit CLEARS both cholesterol fields rather than converting
   * what is in them.
   *
   * Converting would be friendlier and is the wrong thing on a page whose
   * output someone may repeat to a doctor: nobody reading the form afterwards
   * can tell a converted number from a typed one. Empty boxes ask for the two
   * figures that are actually on the reader's report.
   */
  function selectCholUnit(next: LipidUnit) {
    if (next === cholUnit) return
    setCholUnit(next)
    setTotalCholesterol("")
    setHdlCholesterol("")
    invalidate()
  }

  /**
   * The two fields' bounds and placeholders in whichever unit is being asked
   * for. `min`/`max` have to follow the unit, or the browser's own stepper
   * walks a mg/dL entry straight out of the accepted range.
   */
  const bound = (mmolPerL: number) =>
    Math.round(fromMmolPerL(mmolPerL, cholUnit, CHOLESTEROL_MG_DL_PER_MMOL_L))
  const inMgDl = cholUnit === "mgPerDl"
  const cholStep = inMgDl ? "1" : "0.1"
  const totalCholPlaceholder = inMgDl ? "245" : "6.3"
  const hdlPlaceholder = inMgDl ? "54" : "1.4"

  const hasInput =
    age.trim() !== "" ||
    systolicBp.trim() !== "" ||
    totalCholesterol.trim() !== "" ||
    hdlCholesterol.trim() !== ""

  /** The six inputs as numbers, or null while any measured field is blank. */
  const measurements = useMemo(() => {
    const years = parseField(age)
    const sbp = parseField(systolicBp)
    const tc = parseField(totalCholesterol)
    const hdl = parseField(hdlCholesterol)
    if (
      !Number.isFinite(years) ||
      !Number.isFinite(sbp) ||
      !Number.isFinite(tc) ||
      !Number.isFinite(hdl)
    ) {
      return null
    }
    return {
      sex,
      age: years,
      smoker,
      systolicBp: sbp,
      // The two converted inputs on this page. SCORE2's terms are mmol/L, so
      // the market's unit stops here.
      totalCholesterol: toMmolPerL(tc, cholUnit, CHOLESTEROL_MG_DL_PER_MMOL_L),
      hdlCholesterol: toMmolPerL(hdl, cholUnit, CHOLESTEROL_MG_DL_PER_MMOL_L),
    }
  }, [sex, smoker, age, systolicBp, totalCholesterol, hdlCholesterol, cholUnit])

  /**
   * Plausibility is checked here rather than by disabling the button: a
   * disabled control with no explanation leaves the reader guessing, so the
   * button enables as soon as every field is filled and an unscoreable entry
   * gets the message that explains which of the three things went wrong.
   *
   * The risk is rounded first and banded from that same rounded value (see
   * `roundRisk`) so the label always describes the number on screen.
   */
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!measurements || !region) return
    // Against the RAW entries, not the converted ones: what makes this a
    // mistake is the number the reader typed beside the unit they had
    // selected, and the conversion has already hidden that. Total cholesterol
    // is the reliable tell — an HDL in mmol/L and an HDL in mg/dL can both be
    // small — so it decides for the pair, which is what one shared switch
    // means.
    if (
      looksLikeMmolPerLEntry(
        parseField(totalCholesterol),
        cholUnit,
        MIN_TOTAL_CHOLESTEROL,
        CHOLESTEROL_MG_DL_PER_MMOL_L
      )
    ) {
      setResult(null)
      setProblem("cholMmolValue")
      track.calculatorResult({
        calculator: "score2",
        outcome: "rejected",
        reason: "cholMmolValue",
      })
      return
    }
    const found = score2Problem(measurements)
    if (found !== null) {
      setResult(null)
      setProblem(found)
      track.calculatorResult({
        calculator: "score2",
        outcome: "rejected",
        reason: found,
      })
      return
    }
    const risk = roundRisk(score2Risk(measurements, region))
    setProblem(null)
    track.calculatorResult({ calculator: "score2", outcome: "result" })
    setResult({
      risk,
      band: score2BandFor(measurements.age, risk),
      ageGroup: score2AgeGroupFor(measurements.age),
    })
  }

  function reset() {
    setAge("")
    setSystolicBp("")
    setTotalCholesterol("")
    setHdlCholesterol("")
    invalidate()
  }

  const styles = result ? BAND_STYLES[result.band] : null

  /**
   * Segment widths on the 0-to-30 axis, in percentage points of risk, taken
   * from the reader's own ESC age group: the same 6% is high risk at 55 and
   * very high at 45, so a fixed set of boundaries would mislabel the bar under
   * half of all results.
   */
  const segments = result
    ? [
        // no-raw-ids: band lookup key, never rendered
        { id: "lowToModerate" as const, span: result.ageGroup.highFrom },
        {
          // no-raw-ids: band lookup key, never rendered
          id: "high" as const,
          span: result.ageGroup.veryHighFrom - result.ageGroup.highFrom,
        },
        {
          // no-raw-ids: band lookup key, never rendered
          id: "veryHigh" as const,
          span: SCALE_MAX - result.ageGroup.veryHighFrom,
        },
      ]
    : []

  const markerPercent = result
    ? Math.min(100, Math.max(0, (result.risk / SCALE_MAX) * 100))
    : 0

  const problemMessage = problem
    ? problem === "ageOutOfRange"
      ? t(k.calcAgeOutOfRange)
      : problem === "hdlAboveTotal"
        ? t(k.calcHdlAboveTotal)
        : problem === "cholMmolValue"
          ? t(k.calcCholMmolValue)
          : t(k.calcOutOfRange)
    : null

  return (
    <div
      data-testid="score2-calculator"
      data-hydrated={hydrated ? "true" : undefined}
      /* One centred column at the prose measure, lining up with the
         sections below it. The result no longer needs a column of its
         own: it does not exist until Calculate has run. */
      className="mx-auto w-full max-w-[760px]"
    >
      {/* ── Intro ── */}
      <div className="text-center">{intro}</div>

      {/* ── Form ── */}
      {/*
        `noValidate` is load-bearing, not a style choice.

        The number inputs below carry `min`/`max` so a phone shows a sensible
        keypad and a stepper cannot walk outside the model's range. But those
        attributes also arm the browser's own constraint validation, which
        runs BEFORE `onSubmit` and cancels the submit outright on a violation.
        The effect is that typing 74 into the age field did nothing at all:
        `handleSubmit` never ran, `problem` was never set, and the result
        panel sat on its empty-state line as if the button were inert. A CI
        Playwright run caught it; by eye it looks like a dead button.

        It also has to be `noValidate` rather than dropping `min`/`max`,
        because the browser's bubble cannot say what these messages say. "Value
        must be less than or equal to 69" is wrong about why: a 74-year-old has
        not mistyped anything, they need to be pointed at SCORE2-OP. Same for
        HDL above total cholesterol, which no per-field constraint can express.
      */}
      <form onSubmit={handleSubmit} noValidate className="mt-10">
        <h2 className="type-title-lg text-center text-neutral-900">
          {t(k.calcTitle)}
        </h2>

        <div className="mt-6 space-y-4">
          {/* Sex — a real radiogroup, styled as a segmented control. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-sex-label`}>
            <span id={`${fieldId}-sex-label`} className={labelClasses}>
              {t(k.calcSexLabel)}
            </span>
            <div className="mt-2 flex gap-2">
              {SEX_OPTIONS.map((option) => {
                const selected = sex === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`score2-sex-${option.value}`}
                    onClick={() => {
                      setSex(option.value)
                      invalidate()
                    }}
                    className={cn(
                      "type-title-sm flex-1 cursor-pointer rounded-full px-4 py-2.5 transition-colors",
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Current smoker — the model has no former-smoker term, so the
              control is a yes/no rather than three states it cannot use. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-smoker-label`}>
            <span id={`${fieldId}-smoker-label`} className={labelClasses}>
              {t(k.calcSmokerLabel)}
            </span>
            <div className="mt-2 flex gap-2">
              {SMOKER_OPTIONS.map((option) => {
                const selected = smoker === (option.value === "yes")
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`score2-smoker-${option.value}`}
                    onClick={() => {
                      setSmoker(option.value === "yes")
                      invalidate()
                    }}
                    className={cn(
                      "type-title-sm flex-1 cursor-pointer rounded-full px-4 py-2.5 transition-colors",
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${fieldId}-age`} className={labelClasses}>
                {t(k.calcAgeLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-age`}
                  data-testid="score2-age"
                  type="number"
                  inputMode="numeric"
                  min={SCORE2_MIN_AGE}
                  max={SCORE2_MAX_AGE}
                  step="1"
                  autoComplete="off"
                  placeholder="55"
                  value={age}
                  onChange={(e) => {
                    setAge(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-20")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitYears)}</span>
              </div>
            </div>
            <div>
              <label htmlFor={`${fieldId}-sbp`} className={labelClasses}>
                {t(k.calcSbpLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-sbp`}
                  data-testid="score2-sbp"
                  type="number"
                  inputMode="numeric"
                  min={80}
                  max={220}
                  step="1"
                  autoComplete="off"
                  placeholder="140"
                  value={systolicBp}
                  onChange={(e) => {
                    setSystolicBp(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-20")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitMmhg)}</span>
              </div>
            </div>
          </div>
          <p className={hintClasses}>{t(k.calcSbpHint)}</p>

          {/*
            The unit both cholesterol fields ask for, where the market's
            labs and the model disagree (lib/lipid-units.ts). Above the row
            rather than beside one label, because it governs the pair.
          */}
          {canSwitchCholUnit && (
            <div
              role="radiogroup"
              aria-label={t(k.calcCholUnitLabel)}
              className="flex justify-end gap-1"
            >
              {CHOL_UNIT_OPTIONS.map((option) => {
                const selected = cholUnit === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`score2-chol-unit-${option.value}`}
                    onClick={() => selectCholUnit(option.value)}
                    className={cn(
                      "type-label-md cursor-pointer rounded-full px-3 py-1 normal-case transition-colors",
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor={`${fieldId}-total-cholesterol`}
                className={labelClasses}
              >
                {t(k.calcTotalCholLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-total-cholesterol`}
                  data-testid="score2-total-cholesterol"
                  data-unit={cholUnit}
                  type="number"
                  inputMode="decimal"
                  min={bound(MIN_TOTAL_CHOLESTEROL)}
                  max={bound(MAX_TOTAL_CHOLESTEROL)}
                  step={cholStep}
                  autoComplete="off"
                  placeholder={totalCholPlaceholder}
                  value={totalCholesterol}
                  onChange={(e) => {
                    setTotalCholesterol(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-24")}
                />
                <span className={unitSuffixClasses}>
                  {inMgDl ? t(k.calcUnitMgDl) : t(k.calcUnitMmol)}
                </span>
              </div>
            </div>
            <div>
              <label htmlFor={`${fieldId}-hdl`} className={labelClasses}>
                {t(k.calcHdlLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-hdl`}
                  data-testid="score2-hdl"
                  data-unit={cholUnit}
                  type="number"
                  inputMode="decimal"
                  min={bound(MIN_HDL)}
                  max={bound(MAX_HDL)}
                  step={cholStep}
                  autoComplete="off"
                  placeholder={hdlPlaceholder}
                  value={hdlCholesterol}
                  onChange={(e) => {
                    setHdlCholesterol(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-24")}
                />
                <span className={unitSuffixClasses}>
                  {inMgDl ? t(k.calcUnitMgDl) : t(k.calcUnitMmol)}
                </span>
              </div>
            </div>
          </div>
          <p className={hintClasses}>
            {inMgDl ? t(k.calcCholHintMgDl) : t(k.calcCholHint)}
          </p>
        </div>

        <div className="mt-6 flex items-center gap-4">
          {/*
            `buttonVariants` on a bare <button> rather than <Button>: the
            idiom the other two calculators use, and it keeps `data-testid`
            off a custom component's prop type.
          */}
          <button
            type="submit"
            data-testid="score2-submit"
            disabled={measurements === null || !region}
            className={cn(buttonVariants({ variant: "primary" }), "flex-1")}
          >
            {t(k.calcSubmit)}
          </button>
          {hasInput && (
            <button
              type="button"
              onClick={reset}
              className="type-title-sm shrink-0 cursor-pointer text-neutral-500 underline underline-offset-4 transition-colors hover:text-neutral-900"
            >
              {t(k.calcReset)}
            </button>
          )}
        </div>

        {/* Who the model applies to, next to the button rather than buried
            further down the page: a reader with diabetes or established
            heart disease needs to know before they read a number, not
            after. */}
        <p className={hintClasses}>{t(k.calcEligibilityNote)}</p>

        {/*
          The refusal message used to be the result panel's status line. With
          that panel gone until Calculate succeeds, it belongs beside the
          control that produced it. `data-state` still names WHICH refusal is
          showing, so a test can tell them apart without asserting copy in
          three languages — and there is no longer an "empty" value for a
          green-looking spec to pass on by accident.
        */}
        {problemMessage !== null && (
          <p
            data-testid="score2-status"
            data-state={problem}
            className="type-body-md text-terracotta-700 mt-4 text-center"
          >
            {problemMessage}
          </p>
        )}
      </form>

      {/* ── Result ── */}
      {/*
        The live region is mounted from the start so the panel is
        announced when it arrives; the panel itself only exists once
        Calculate has produced a figure, which is the point of the
        layout.
      */}
      <div aria-live="polite">
        {result !== null && styles !== null && (
          <div
            data-testid="score2-result"
            className="mt-8 rounded-[16px] bg-white p-6 md:p-8"
          >
            <div>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="type-label-md text-neutral-500 normal-case">
                    {t(k.calcResultLabel)}
                  </p>
                  <p
                    data-testid="score2-value"
                    className={cn(
                      "type-display-md mt-1 tabular-nums",
                      styles.accent
                    )}
                  >
                    {formatDecimal(result.risk, 1, locale)}
                    {t(k.calcResultSuffix)}
                  </p>
                </div>
                <span
                  data-testid="score2-band"
                  // The band as a value rather than as a word, so a test can
                  // assert it in a locale whose three labels share no substring
                  // with English. The FIB-4, sMAF-5 and MAFLD widgets already
                  // stamp theirs; this one did not need it while every market
                  // it served wrote "high" recognisably, and the Dutch pages
                  // are where that stopped being true ("Hoog risico").
                  data-band={result.band}
                  className={cn(
                    "type-title-sm rounded-full px-4 py-1.5",
                    styles.chip
                  )}
                >
                  {t(SCORE2_BANDS[result.band].labelKey)}
                </span>
              </div>

              {/* Scale */}
              <div className="mt-6">
                {/* The age group rides in parentheses rather than being glued
                    onto the end of the label: the group labels are noun phrases
                    ("under 50", "moins de 50") that read as a fragment after a
                    preposition in French and German, and a second key per group
                    just to carry the preposition would be three more strings to
                    keep in step with the table's row headers. */}
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcThresholdsLabel)} ({t(result.ageGroup.labelKey)})
                </p>
                <div className="relative mt-3">
                  {/*
                    Contiguous, no gap between segments. A gap would push every
                    boundary right of its true position while the marker is
                    placed on the ungapped percentage, so the marker would stop
                    landing on the band it names. Rounding lives on the clipping
                    container instead of the individual segments.
                  */}
                  <div className="flex h-2 w-full overflow-hidden rounded-full">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        className={cn("h-full", BAND_STYLES[segment.id].bar)}
                        style={{
                          width: `${(segment.span / SCALE_MAX) * 100}%`,
                        }}
                      />
                    ))}
                  </div>
                  <div
                    aria-hidden
                    className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-neutral-900 bg-white transition-[left] duration-300 ease-out"
                    style={{ left: `${markerPercent}%` }}
                  />
                </div>
                {/*
                  Ticks are absolutely positioned at their real place on the
                  axis. The two thresholds are not evenly spaced (5% sits at 17%
                  of the 0-to-30 axis, 10% at 33%), so a flex justify-between row
                  would print them at even intervals and mislabel the bands
                  underneath.
                */}
                <div aria-hidden className="relative mt-3 h-4">
                  <span className="type-label-sm absolute top-0 left-0 text-neutral-400 normal-case tabular-nums">
                    0{t(k.calcResultSuffix)}
                  </span>
                  {[result.ageGroup.highFrom, result.ageGroup.veryHighFrom].map(
                    (value) => (
                      <span
                        key={value}
                        className="type-label-sm absolute top-0 -translate-x-1/2 text-neutral-400 normal-case tabular-nums"
                        style={{ left: `${(value / SCALE_MAX) * 100}%` }}
                      >
                        {formatDecimal(value, value % 1 === 0 ? 0 : 1, locale)}
                        {t(k.calcResultSuffix)}
                      </span>
                    )
                  )}
                  <span className="type-label-sm absolute top-0 right-0 text-neutral-400 normal-case tabular-nums">
                    {SCALE_MAX}
                    {t(k.calcResultSuffix)}+
                  </span>
                </div>
                <p className="type-body-sm mt-2 text-neutral-500">
                  {t(k.calcScaleCaption)}
                </p>
              </div>

              <p
                data-testid="score2-meaning"
                className="type-body-md mt-6 border-t border-neutral-200 pt-5 text-neutral-600"
              >
                {t(SCORE2_BANDS[result.band].meaningKey)}
              </p>
            </div>
            {/*
              Check-up hand-off. Inside the result panel and gated on `result`, so
              it is the reward for pressing Calculate rather than another CTA
              sitting on the page from the start. It books the market's blood
              check-up directly (CalculatorBookingCta) rather than sending the
              reader through the quiz first, and it does NOT claim the booking
              reads this result.
            */}
            <div className="bg-aubergine-50 mt-6 rounded-lg p-5">
              <h3 className="type-title-md text-neutral-900">
                {t(k.quizHeading)}
              </h3>
              <p className="type-body-md mt-2 text-neutral-600">
                {t(k.quizBody)}
              </p>
              <CalculatorBookingCta calculator="score2">
                {t(k.quizCta)}
              </CalculatorBookingCta>
            </div>
          </div>
        )}
      </div>

      {/* The caveat is about the tool, not the answer, so it stays on
          the page whether or not anything has been calculated. */}
      <p className="type-body-sm mx-auto mt-8 max-w-[600px] text-center text-neutral-500">
        {t(k.calcDisclaimer)}
      </p>
    </div>
  )
}
